import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import React, {useEffect, useState} from 'react';
import {useDispatch, useIntl, useSelector} from 'umi';
import ProForm from '@ant-design/pro-form';
import {Button, Form, Space} from 'antd';

import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import UserDetails from '@/components/UserDrawer/Edit/UserDetails';
import * as UsersSelectors from '@/selectors/usersSelectors';
import {createUserRequest} from '@/services/api/user';
import {processAPIResponseNow} from '@/utils/request';
import {useHasAccess} from '@/utils/hooks';
import PERMISSIONS from '@/constants/permissions';

import styles from '@/components/UserDrawer/index.less';

const UserCreateDrawer: React.FC = () => {
  const dispatch = useDispatch();
  const canViewUser = useHasAccess([PERMISSIONS.Users.VIEW, PERMISSIONS.BUSINESS_PARTNER]);
  const [form] = Form.useForm();

  const visible = useSelector(UsersSelectors.selectDrawerVisible);
  const drawer = useSelector(UsersSelectors.selectCreateUserDrawer);
  const onCreate = drawer?.onCreate;
  const initialValues = drawer?.initialValues;
  const [createUserLoading, setCreateUserLoading] = useState(false);

  function toggle() {
    dispatch({
      type: 'user/toggleDrawer',
    });
  }

  useEffect(() => {
    dispatch({
      type: 'user/fetchConversionalRoles',
      payload: {
        transferable: 1,
      },
    });
  }, []);

  const parseSearchPhrase = (phrase: string) => {
    if (!phrase) return {};

    if (phrase.includes('@')) {
      return { email: phrase };
    }

    const isNumeric = /^\+?[\d\s\-()]+$/.test(phrase);
    if (isNumeric && phrase.replace(/[^\d]/g, '').length >= 7) {
      return { phone: phrase };
    }

    return { firstname: phrase };
  };

  useEffect(() => {
    if (visible) {
      if (initialValues?.searchValue) {
        const parsed = parseSearchPhrase(initialValues.searchValue);
        form.setFieldsValue(parsed);
      } else {
        form.resetFields();
      }
    }
  }, [visible, initialValues, form]);

  useEffect(() => {
    if (!visible)
      form.resetFields();
  }, [visible]);

  function onCreateUser() {

    form.validateFields().then(values => {
      setCreateUserLoading(true);
      createUserRequest({ payload: values }).then(Response => {
        setCreateUserLoading(false);
        processAPIResponseNow(Response, {
          onSuccess: (user) => {
            if (onCreate) {
              onCreate(Response);
            }
            if (!drawer.notOpenUserDetailsDrawer && canViewUser){
              dispatch({
                type: 'user/openAgentDetailsDrawer',
                payload: {
                  id: user.id,
                },
              });
            }
            setTimeout(() => {
              toggle();
            }, 200);

            dispatch({
              type: 'user/getAllUsers',
            });
            dispatch({
              type: 'leads/fetchAssignees',
            });
          },
        }, 'data');
      });
    });
  }

  const intl = useIntl();

  const submitOnEnter = useSubmitOnEnter(onCreateUser, {
    enabled: !createUserLoading,
  });

  function getFooter() {
    return (
      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button
            className={'button-secondary button-bordered'}
            size={'large'}
            type={'primary'}
            onClick={toggle}
          >
            <F id={'common.actions.close'} defaultMessage={'Close'} />
          </Button>

          <Button
            size={'large'}
            loading={createUserLoading}
            onClick={onCreateUser}
            type={'primary'}
          >
            <F entity={INTL_ENTITY.USER} action={INTL_ACTION.ADD} />
          </Button>
        </Space>
      </div>
    );
  }

  return (
    <div className={styles.drawerContainer}>
      <Drawer
        width={520}
        footer={getFooter()}
        title={intl.formatMessage({
          id: 'pages.users.newUser',
          defaultMessage: 'New User',
        })}
        open={visible}
        destroyOnHidden
        onOpenChange={toggle}
      >
        <ProForm {...submitOnEnter} form={form} submitter={false}>
          <UserDetails
            isCreateMode={true}
            useTransferableRoles={true}
          />
        </ProForm>
      </Drawer>
    </div>
  );
};

export default React.memo(UserCreateDrawer, () => true);
